/**
 * Canonical valuation progress snapshot.
 *
 * The backend owns `overallPercent`. Clients should render this number
 * directly rather than re-deriving percent from phase names. The number
 * is monotonic (clients never see it move backwards) and never reaches
 * 100 until the report status is terminal `completed`.
 *
 * This module contains pure types only — no runtime dependencies — so it
 * is safe to import from server jobs, tRPC procedures, and React client
 * components alike.
 */

/** Lifecycle status used for both the overall report and individual phases / units. */
export type ProgressStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

/** Canonical phase identifiers. The order is the order of execution. */
export type ProgressPhaseId =
  | "intake"
  | "reading"
  | "understanding"
  | "extraction"
  | "reconciliation"
  | "data_gathering"
  | "research"
  | "valuation"
  | "comparables"
  | "report";

/**
 * One unit of work within a phase. Used to surface "what the analyst is
 * doing right now" in the UI: a document being read, a model running, a
 * page batch being OCR'd, a tool call etc.
 */
export interface ProgressUnit {
  id: string;
  label: string;
  kind: "document" | "page_batch" | "model" | "tool" | "phase";
  status: ProgressStatus;
  /** Optional sub-progress, e.g. completed/total pages within an OCR call. */
  completed?: number;
  total?: number;
  /** Free-form per-unit metadata (filename, model role, etc.) for the UI. */
  metadata?: Record<string, unknown>;
  startedAt?: string;
  completedAt?: string;
}

/** Phase-level state used to compute the canonical percent. */
export interface ProgressPhase {
  id: ProgressPhaseId;
  label: string;
  /** Relative weight in overall percent calculation. Sum of weights = 100. */
  weight: number;
  status: ProgressStatus;
  /** completedWeight / totalWeight is the fraction of this phase done. */
  completedWeight: number;
  totalWeight: number;
  startedAt?: string;
  completedAt?: string;
  summary?: string;
}

/** A market-research step the user can manually retry from the processing UI. */
export interface RetryableResearchStep {
  category: string;
  unitId: string;
  label: string;
  error?: string;
}

/** Activity feed entry (e.g. "Read pages 5-10 of P&L 2024.pdf"). */
export interface ProgressActivityEntry {
  id: string;
  ts: string;
  kind: "info" | "success" | "warning" | "error";
  label: string;
  detail?: string;
  phaseId?: ProgressPhaseId;
}

/** Headline counters used for the stat strip on the processing screen. */
export interface ProgressCounters {
  documentsTotal: number;
  documentsRead: number;
  pagesOcred: number;
  recordsExtracted: number;
  eventsExtracted: number;
  conflictsResolved: number;
  conflictsAwaitingUser: number;
  modelsRun: number;
  comparablesFound: number;
  externalSourcesUsed: number;
  teamMembersFound: number;
}

export interface ProgressEta {
  elapsedMs: number;
  estimatedRemainingMs: number | null;
}

/** Canonical, backend-owned snapshot of valuation progress. */
export interface ProgressSnapshot {
  /** Schema version. Bump when shape changes incompatibly. */
  version: 2;
  /** 0-100, monotonic, capped at 99 until status is terminal completed. */
  overallPercent: number;
  status: ProgressStatus;
  currentPhaseId: ProgressPhaseId | null;
  currentAction: { title: string; subtitle?: string } | null;
  /** Currently in-flight units (typically 1-3 entries). */
  activeUnits: ProgressUnit[];
  /** Most-recent N activity entries, newest first. */
  recentActivity: ProgressActivityEntry[];
  counters: ProgressCounters;
  eta: ProgressEta | null;
  phases: ProgressPhase[];
  /** Backend updatedAt for this snapshot, ISO. */
  updatedAt: string;
  /**
   * Legacy phase mirror. The old UI hook reads `phases` with names like
   * "Upload & Extraction" — keep emitting this until all clients are upgraded.
   */
  legacyPhases?: {
    name: string;
    status: ProgressStatus;
    summary?: string;
    startedAt?: string;
    completedAt?: string;
  }[];
  /** Optional legacy file progress for old clients. */
  legacyFileProgress?: unknown;
  /** Market-research steps that failed and can be retried individually. */
  retryableResearch?: RetryableResearchStep[];
}

/** Default phase definitions and weights. Weights must sum to 100. */
export const DEFAULT_PHASES: readonly Omit<
  ProgressPhase,
  "status" | "completedWeight" | "totalWeight"
>[] = [
  { id: "intake", label: "Intake & storage", weight: 6 },
  { id: "reading", label: "Reading documents", weight: 22 },
  { id: "understanding", label: "Understanding documents", weight: 8 },
  { id: "extraction", label: "Extracting data", weight: 18 },
  { id: "reconciliation", label: "Reconciling data", weight: 6 },
  { id: "data_gathering", label: "Gathering business details", weight: 4 },
  { id: "research", label: "Researching market conditions", weight: 6 },
  { id: "valuation", label: "Running valuation models", weight: 18 },
  { id: "comparables", label: "Finding comparable sales", weight: 4 },
  { id: "report", label: "Assembling your report", weight: 8 },
] as const;

/** Mapping between new phase ids and the legacy human-facing phase names. */
export const PHASE_TO_LEGACY_NAME: Record<ProgressPhaseId, string> = {
  intake: "Upload & Extraction",
  reading: "Upload & Extraction",
  understanding: "AI Classification",
  extraction: "Upload & Extraction",
  reconciliation: "Conflict Resolution",
  data_gathering: "Data Gathering",
  research: "External Research",
  valuation: "Valuation Computation",
  comparables: "Comparable Sales",
  report: "Report Assembly",
};

export function emptyCounters(): ProgressCounters {
  return {
    documentsTotal: 0,
    documentsRead: 0,
    pagesOcred: 0,
    recordsExtracted: 0,
    eventsExtracted: 0,
    conflictsResolved: 0,
    conflictsAwaitingUser: 0,
    modelsRun: 0,
    comparablesFound: 0,
    externalSourcesUsed: 0,
    teamMembersFound: 0,
  };
}

export function emptySnapshot(): ProgressSnapshot {
  return {
    version: 2,
    overallPercent: 0,
    status: "pending",
    currentPhaseId: null,
    currentAction: null,
    activeUnits: [],
    recentActivity: [],
    counters: emptyCounters(),
    eta: null,
    phases: DEFAULT_PHASES.map((p) => ({
      ...p,
      status: "pending" as ProgressStatus,
      completedWeight: 0,
      totalWeight: 1,
    })),
    updatedAt: new Date().toISOString(),
    retryableResearch: [],
  };
}

/**
 * Compute the canonical overall percent from phases, capped at 99 until the
 * caller marks the snapshot as terminally completed. The result is a whole
 * number to avoid jitter in the UI.
 */
export function computeOverallPercent(
  phases: ProgressPhase[],
  isTerminalComplete: boolean,
): number {
  if (phases.length === 0) return 0;
  if (isTerminalComplete) return 100;

  let earned = 0;
  let totalWeight = 0;
  for (const phase of phases) {
    totalWeight += phase.weight;
    if (phase.status === "completed") {
      earned += phase.weight;
    } else if (phase.status === "running") {
      const safeTotal = Math.max(phase.totalWeight, 1);
      const fraction = Math.min(Math.max(phase.completedWeight / safeTotal, 0), 1);
      earned += phase.weight * fraction;
    }
  }

  if (totalWeight <= 0) return 0;
  const pct = Math.round((earned / totalWeight) * 100);
  return Math.min(99, Math.max(0, pct));
}
